import { dehydrate, HydrationBoundary, useQuery } from '@tanstack/react-query'
import { useQueryState } from 'nuqs'
import { useEffect } from 'react'
import { type ColorSpace } from 'react-aria-components'
import {
	type LoaderFunctionArgs,
	Outlet,
	useLoaderData,
	useLocation,
	useRouteLoaderData,
} from 'react-router'
import framerLogo from '#app/assets/notionicons/framer-logo.png'
import framericonsPluginDark from '#app/assets/notionicons/framericons-plugin-dark.png'
import framericonsPluginLight from '#app/assets/notionicons/framericons-plugin-light.png'
import { CustomizePanel } from '#app/components/customize-panel.tsx'
import { IconSetErrorBoundary } from '#app/components/icon-set-error-boundary.tsx'
import { ImpersonationBanner } from '#app/components/impersonation-banner.js'
import { Button } from '#app/components/shared/buttons/button.tsx'
import { SidebarNavigationSectionsSubheadings } from '#app/components/sidebar-navigation.tsx'
import { createServerCaller } from '#app/lib/trpc-server.ts'
import { makeQueryClient, trpc } from '#app/lib/trpc-tanstack.tsx'
import { searchParamsParsers } from '#app/utils/search-params.js'
import { useWorkspaceId } from '#app/utils/user.js'

export async function loader({ request }: LoaderFunctionArgs) {
	const queryClient = makeQueryClient()
	const caller = createServerCaller(request)

	// Prefetch user first for SSR hydration
	const userData = await queryClient.fetchQuery({
		queryKey: trpc.user.getCurrent.queryKey(),
		queryFn: () => caller.user.getCurrent(),
	})
	const workspaceId = userData?.memberships[0]?.workspace.id

	// Prefetch using server caller (direct DB calls, no HTTP)
	await Promise.all([
		queryClient.prefetchQuery({
			queryKey: trpc.iconSets.getPublicTree.queryKey(),
			queryFn: () => caller.iconSets.getPublicTree(),
		}),
		queryClient.prefetchQuery({
			queryKey: trpc.iconSets.getPublicUploadedTree.queryKey({ workspaceId }),
			queryFn: () => caller.iconSets.getPublicUploadedTree({ workspaceId }),
		}),
		// Prefetch smart search status for public icon sets
		queryClient.prefetchQuery({
			queryKey: trpc.iconSets.getSmartSearchStatusBatchPublic.queryKey(),
			queryFn: () => caller.iconSets.getSmartSearchStatusBatchPublic(),
		}),
		// Prefetch smart search status for uploaded sets (if authenticated)
		...(workspaceId
			? [
					queryClient.prefetchQuery({
						queryKey: trpc.uploaded.getSmartSearchStatusBatch.queryKey(),
						queryFn: () => caller.uploaded.getSmartSearchStatusBatch(),
					}),
				]
			: []),
		// Prefetch color palettes for authenticated users
		...(userData
			? [
					queryClient.prefetchQuery({
						queryKey: trpc.colorPalette.list.queryKey(),
						queryFn: () => caller.colorPalette.list(),
					}),
				]
			: []),
	])

	return { dehydratedState: dehydrate(queryClient) }
}

export async function clientLoader() {
	return null
}

export default function PublicLayout() {
	const loaderData = useLoaderData<typeof loader | typeof clientLoader>()

	return (
		<HydrationBoundary state={loaderData?.dehydratedState}>
			<PublicLayoutContent />
		</HydrationBoundary>
	)
}

function PublicLayoutContent() {
	const workspaceId = useWorkspaceId()
	const location = useLocation()
	const isRecentlyUsed = location.pathname.includes('/recently-used')
	const rootData = useRouteLoaderData('root') as
		| { domainConfig?: { name?: string } }
		| undefined
	const siteName = rootData?.domainConfig?.name ?? 'Iconited'

	// No initialData needed - data is already in cache from HydrationBoundary
	const { data: publicTreeData } = useQuery(
		trpc.iconSets.getPublicTree.queryOptions(),
	)
	// Uploaded tree fetched client-side (user-specific, refetched on auth change)
	const { data: uploadedTreeData, isLoading: isLoadingUploaded } = useQuery(
		trpc.iconSets.getPublicUploadedTree.queryOptions({ workspaceId }),
	)
	// Poll smart search status (backend auto-ensures processing) and this stops polling when all are ready.
	// refetchOnWindowFocus: 'always' while processing bypasses staleTime as a safety net for paused polling.
	useQuery({
		...trpc.iconSets.getSmartSearchStatusBatchPublic.queryOptions(),
		refetchOnWindowFocus: (query) =>
			query.state.data?.some((s) => !s.ready) ? 'always' : true,
		refetchInterval: (query) =>
			query.state.data?.some((s) => !s.ready) ? 30000 : false,
	})
	useQuery({
		...trpc.uploaded.getSmartSearchStatusBatch.queryOptions(),
		enabled: !!workspaceId,
		refetchOnWindowFocus: (query) =>
			query.state.data?.some((s) => !s.ready) ? 'always' : true,
		refetchInterval: (query) =>
			query.state.data?.some((s) => !s.ready) ? 30000 : false,
	})

	const tree = publicTreeData?.tree ?? []
	const uploadedTree = uploadedTreeData?.tree ?? []

	const [darkMode] = useQueryState('darkMode', searchParamsParsers.darkMode)
	const [color, setColor] = useQueryState('color', searchParamsParsers.color)
	const [colorSpace, setColorSpace] = useQueryState(
		'colorSpace',
		searchParamsParsers.colorSpace,
	)
	const [strokeWidth, setStrokeWidth] = useQueryState(
		'strokeWidth',
		searchParamsParsers.strokeWidth,
	)

	useEffect(() => {
		document.documentElement.classList.toggle('dark-mode', darkMode === 'true')
	}, [darkMode])

	return (
		<>
			<ImpersonationBanner />
			<div className="bg-primary flex flex-col lg:flex-row">
				<SidebarNavigationSectionsSubheadings
				tree={tree}
				uploadedTree={uploadedTree}
				isLoadingUploaded={isLoadingUploaded}
				strokeWidth={String(strokeWidth)}
				setStrokeWidth={(value) => setStrokeWidth(Number(value))}
				color={color}
				setColor={setColor}
				colorSpace={colorSpace as ColorSpace | 'hex'}
				setColorSpace={setColorSpace}
			/>

			<main className="bg-primary min-w-0 flex-1 pt-8 pb-12">
				<div className="flex flex-col gap-8">
					<div className="flex flex-row-reverse gap-10 px-4 lg:px-8">
						<div className="max-lg:hidden">
							<div className="sticky top-0 mt-8 pt-[25px]">
								<div className="border-secondary bg-primary shadow-xs md:border-r lg:rounded-xl lg:border">
									<div className="flex min-h-0 flex-col">
										<CustomizePanel
											strokeWidth={String(strokeWidth)}
											setStrokeWidth={(value) => setStrokeWidth(Number(value))}
											color={color}
											setColor={setColor}
											colorSpace={colorSpace as ColorSpace | 'hex'}
											setColorSpace={setColorSpace}
											hideColorStroke={isRecentlyUsed}
										/>
									</div>
								</div>
								<aside className="isolate mt-8">
									<div className="relative">
										<p className="text-primary text-center text-sm leading-snug font-semibold">
											All your favorite icon sets in one
											<img
												className="mx-px inline-block h-4 w-4 align-text-bottom"
												src={framerLogo}
												alt=""
											/>
											Framer plugin
										</p>
										<img
											className="border-secondary bg-primary relative -z-1 mt-3 w-2xs min-w-full shadow-xs md:border-r lg:rounded-xl lg:border"
											src={
												darkMode === 'true'
													? framericonsPluginDark
													: framericonsPluginLight
											}
											alt="Framericons plugin preview"
										/>
										<Button
											href="https://framer.link/oENxiQs"
											isExternal
											color="secondary"
											size="lg"
											className="static -mt-8 w-full after:absolute after:inset-0"
										>
											Use {siteName}
										</Button>
									</div>
								</aside>
							</div>
						</div>
						<div className="mx-auto max-w-360 flex-1">
							<Outlet />
						</div>
					</div>
				</div>
			</main>
			</div>
		</>
	)
}

export { IconSetErrorBoundary as ErrorBoundary }
